import React, { useState, useRef, useEffect } from "react";
import { Section, ContentBlock, Headline } from "./ui";

const ImageCompare = ({
  beforeSrc,
  afterSrc,
  title = "Vorher / Nachher",
  leftLabel = "Vorher",
  rightLabel = "Nachher",
  variant = "transparent",
  spacing = "comfortable",
  className = "",
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Event handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateSliderPosition(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    updateSliderPosition(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateSliderPosition = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    updateSliderPositionTouch(e);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    updateSliderPositionTouch(e);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const updateSliderPositionTouch = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  // Effect for global event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    const handleGlobalTouchMove = (e) => handleTouchMove(e);
    const handleGlobalTouchEnd = () => handleTouchEnd();

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDragging]);

  // Add safety checks for required props
  if (!beforeSrc || !afterSrc) {
    console.error("ImageCompare: beforeSrc and afterSrc are required props");
    return (
      <Section variant={variant} spacing={spacing} className={className}>
        <ContentBlock maxWidth="4xl" spacing="comfortable" className="mx-auto">
          <Headline
            variant="h4"
            color={variant === "primary" ? "secondary" : "primary"}
            className="mb-6"
          >
            {title}
          </Headline>
          <div className="bg-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">
              Bilder können nicht geladen werden. Bitte prüfen Sie die
              Bildpfade.
            </p>
          </div>
        </ContentBlock>
      </Section>
    );
  }

  return (
    <Section variant={variant} spacing={spacing} className={className}>
      <ContentBlock maxWidth="4xl" spacing="comfortable" className="mx-auto">
        <Headline
          variant="h3"
          color={variant === "primary" ? "secondary" : "primary"}
          className="mt-12 sm:mt-20 text-left"
        >
          {title}
        </Headline>
        <div
          ref={containerRef}
          className={`relative overflow-hidden shadow-lg select-none min-h-[375px] md:min-h-0 md:aspect-video ${
            isDragging ? "cursor-grabbing" : "cursor-col-resize"
          }`}
        >
          {/* After Image (full background) */}
          <div className="absolute inset-0">
            <img
              src={afterSrc}
              alt={rightLabel}
              className="w-full h-full object-cover"
              draggable={false}
            />
            <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm">
              {rightLabel}
            </div>
          </div>

          {/* Before Image (clipped from right) */}
          <div
            className="absolute inset-0"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            <img
              src={beforeSrc}
              alt={leftLabel}
              className="w-full h-full object-cover"
              draggable={false}
            />
            <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm">
              {leftLabel}
            </div>
          </div>

          {/* Slider Line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white z-5"
            style={{
              left: `${sliderPosition}%`,
              transform: "translateX(-50%)",
            }}
          />

          {/* Slider Handle */}
          <div
            className={`absolute top-0 bottom-0 w-12 flex items-center justify-center z-10 ${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            style={{
              left: `${sliderPosition}%`,
              transform: "translateX(-50%)",
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div
              className={`w-12 h-12 bg-white rounded-full shadow-xl border-2 border-gray-300 flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-2xl animate-pulse ${
                isDragging
                  ? "scale-110 shadow-2xl ring-4 ring-blue-200 animate-none"
                  : ""
              }`}
            >
              {/* Left Arrow */}
              <svg
                className="w-3 h-3 text-gray-600 absolute left-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>

              {/* Center Grip Lines */}
              <div className="flex flex-col items-center space-y-0.5">
                <div className="w-0.5 h-2 bg-gray-400 rounded-full"></div>
                <div className="w-0.5 h-2 bg-gray-400 rounded-full"></div>
                <div className="w-0.5 h-2 bg-gray-400 rounded-full"></div>
              </div>

              {/* Right Arrow */}
              <svg
                className="w-3 h-3 text-gray-600 absolute right-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </ContentBlock>
    </Section>
  );
};

export default ImageCompare;
